import { useEffect } from "react";
import { AlertCircle, Loader2, RotateCcw, Save } from "lucide-react";
import { LEAVE_MSG, unsavedActions, useUnsavedState } from "@/lib/unsaved";

function useLeaveGuard(dirty) {
  useEffect(() => {
    if (!dirty) return;
    const onUnload = (e) => { e.preventDefault(); e.returnValue = LEAVE_MSG; return LEAVE_MSG; };
    const onClick = (e) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = e.target.closest?.("a[href]");
      if (!a || a.target === "_blank" || a.hasAttribute("download")) return;
      const url = new URL(a.href, window.location.href);
      if (url.origin !== window.location.origin || url.pathname === window.location.pathname) return;
      if (!window.confirm(LEAVE_MSG)) { e.preventDefault(); e.stopPropagation(); }
    };
    window.addEventListener("beforeunload", onUnload);
    document.addEventListener("click", onClick, true);
    return () => { window.removeEventListener("beforeunload", onUnload); document.removeEventListener("click", onClick, true); };
  }, [dirty]);
}

export function UnsavedBar() {
  const { dirty, saving } = useUnsavedState();
  useLeaveGuard(dirty);
  if (!dirty) return null;
  return (
    <div className="sticky bottom-4 z-40 px-4 sm:px-6 lg:px-8 pointer-events-none" role="status" aria-live="polite">
      <div data-testid="unsaved-bar" className="page-enter pointer-events-auto max-w-3xl mx-auto flex items-center gap-3 rounded-xl border border-zinc-700 bg-zinc-900/95 backdrop-blur-md shadow-2xl px-4 py-3">
        <AlertCircle className="w-5 h-5 text-[#f59e0b] shrink-0" />
        <span className="text-sm font-medium flex-1 min-w-0"><span className="hidden sm:inline">You have unsaved changes</span><span className="sm:hidden">Unsaved changes</span></span>
        <button data-testid="unsaved-reset" onClick={unsavedActions.resetAll} disabled={saving} aria-label="Reset changes" title="Reset changes"
          className="flex items-center gap-1.5 text-sm rounded-lg px-3 py-2 text-zinc-300 hover:text-white hover:bg-zinc-800 disabled:opacity-50 transition-colors">
          <RotateCcw className="w-4 h-4" /> <span className="hidden sm:inline">Reset</span>
        </button>
        <button data-testid="unsaved-save" onClick={unsavedActions.saveAll} disabled={saving}
          className="flex items-center gap-1.5 text-sm rounded-lg px-4 py-2 bg-volt text-volt-ink font-semibold hover:bg-volt-hover disabled:opacity-60 transition-colors">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} <span className="hidden sm:inline">Save changes</span><span className="sm:hidden">Save</span>
        </button>
      </div>
    </div>
  );
}
